import { supabase } from "@/integrations/supabase/client";

const trackedExternalLinks = new Set<string>();

export const isExternalUrl = (url: string) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin !== window.location.origin && ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
};

export const annotateExternalAffiliateLinks = (root: ParentNode = document) => {
  const anchors = root.querySelectorAll<HTMLAnchorElement>("a[href]");

  anchors.forEach((anchor) => {
    const href = anchor.getAttribute("href");

    if (!href || !isExternalUrl(href)) {
      return;
    }

    if (!anchor.dataset.skimlinksTracking) {
      anchor.dataset.skimlinksTracking = "hintsly-global";
    }
  });
};

/**
 * Logs a click to the click_logs table and returns immediately
 * so the user isn't blocked from navigating.
 */
export const logAffiliateClick = async ({
  registryId,
  listId,
  productName,
  url,
}: {
  registryId?: string | null;
  listId?: string | null;
  productName: string;
  url: string;
}) => {
  let merchantDomain = "";
  try {
    const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
    merchantDomain = parsed.hostname.replace(/^www\./, "");
  } catch {
    merchantDomain = "unknown";
  }

  // Fire-and-forget — don't block navigation
  supabase.from("click_logs").insert({
    registry_id: registryId ?? null,
    list_id: listId ?? null,
    product_name: productName,
    merchant_domain: merchantDomain,
    url,
  }).then(({ error }) => {
    if (error) {
      // Silently swallow — click logging should never block UX
    }
  });
};

export const logExternalAffiliateAnchor = (anchor: HTMLAnchorElement) => {
  const href = anchor.href;

  if (!isExternalUrl(href) || anchor.dataset.hintslyAffiliateLogged === "manual") {
    return;
  }

  const trackingKey = `${href}::${anchor.textContent?.trim() ?? "external-link"}`;
  if (trackedExternalLinks.has(trackingKey)) {
    return;
  }

  trackedExternalLinks.add(trackingKey);
  window.setTimeout(() => trackedExternalLinks.delete(trackingKey), 1500);

  logAffiliateClick({
    listId: anchor.dataset.skimlinksTracking ?? null,
    productName: anchor.getAttribute("aria-label") || anchor.textContent?.trim() || "External link",
    url: href,
  });
};
